import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';
import { Helmet } from 'react-helmet';
import { Loader } from '@kep-ui-kit/ui-components';

import { withErrorHandling } from '@selfserve/react-hocs';
import {
  useStorage,
  storage,
  storageKeys,
  useQueryParams,
} from '@selfserve/react-hooks';
import {
  StyledContentContainer,
  StyledTitle,
  StyledText,
  SplitScreenLayout,
  StyledVideoScreen,
  StyledSideImage,
  StyledVideoContainer,
  StyledLogo,
} from '@selfserve/react-ui';
import {
  routesPaths,
  getRecaptchaToken,
  IIndexSignature,
  apiStatuses,
  links,
  gtmEvents,
  responseTypes,
  socialType, sendFunnelAnalytics, avatarStudioPackageIds, Packages
} from '@selfserve/react-utils';

import { GetStartedForm } from '../ui/get-started-form';
import { SocialRegistration } from './social-registration';
import { useEmailValidationRequest, usePackageInfoRequest } from '../data';

const ALREADY_REGISTERED_ERROR_ID = 10019;
const EMAIL = 'email';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const StyledGetStartedContainer = styled.div(
  ({ theme, isSecondary, hasSocialLogin }: { theme?: any; isSecondary?: boolean, hasSocialLogin?: boolean }) => ({
    maxWidth: '540px',
    width: '100%',
    ...(isSecondary && {
      [theme.breakpoints.up('sm')]: {
        maxWidth: '684px',
        '.form-container': {
          display: 'flex',
          flexDirection: 'row-reverse',
        },
        '.get-started-form': {
          ...(hasSocialLogin ? {
            width: '50%',
          } : {
            width: '100%',
          }),
          paddingRight: '42px',
        },
        '.oauth-block': {
          width: '50%',
          position: 'relative',
          margin: '0',
          padding: '26px 0 0 41px',
          '.divider': {
            display: 'none',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            width: '2px',
            height: '100%',
            background: theme.palette.grey['500'],
            top: 0,
            left: 0,
          },
        },
      },
    }),
  })
);

const StyledGetStartedTitle = styled(StyledTitle)`
  display: block;
  margin-bottom: 25px;
  text-align: center;
`;

const StyledGetStartedText = styled(StyledText)`
  margin-bottom: 48px;
  text-align: center;
`;

const StyledLoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  background-color: #fff;
  z-index: 9999;
`;

const StyledLoadingHeader = styled.div(({ theme }: { theme?: any }) => ({
  width: '100%',
  height: '60px',
  display: 'flex',
  paddingTop: theme.spacing(1),
  paddingBottom: 0,
  paddingLeft: theme.spacing(2),
  paddingRight: '28px',
  alignItems: 'center',
}));

const StyledLoaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  width: 100%;
`;

const StyledLoader = styled(Loader)(() => ({
  width: '40px',
  height: '40px',
}));

const FadeInContainer = styled.div`
  animation: ${fadeIn} 0.5s ease-in;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const validationSchema = (t: TFunction) =>
  Yup.object().shape({
    email: Yup.string()
      .email(t('common.validations.email-valid'))
      .required(t('common.validations.email-required')),
  });

const onSubmit =
  (
    handleEmailValidationRequest: (
      payload: IIndexSignature<string | undefined>
    ) => void,
    setIsRecaptchaLoading: (value: boolean) => void,
    setSessionPackageId: (value: string) => void,
    setSessionEmail: (value: string) => void,
    desiredPackageId: string | undefined,
    queryParams: IIndexSignature<string>,
    isLoading: boolean
  ) =>
  (values: FormValues) => {
    if (!isLoading) {
      setIsRecaptchaLoading(true);
      getRecaptchaToken().then((recaptchaToken: string) => {
        const payload = {
          ...values,
          ...queryParams,
          desiredPackageId,
          recaptchaToken,
        };
        handleEmailValidationRequest(payload);
        setIsRecaptchaLoading(false);
        setSessionEmail(values.email);
        setSessionPackageId(desiredPackageId as string);
      });
    }
  };

export interface FormValues {
  email: string;
}

interface Props {
  isSecondary?: boolean;
}

export const GetStarted = withErrorHandling<Props>(
  ({ openErrorModal, isSecondary }) => {
    const navigate = useNavigate();
    const { desiredPackageId } = useParams();
    const queryParams = useQueryParams();
    const { t } = useTranslation();
    const [isSocialLoading, setIsSocialLoading] = useState(false);
    const [validateOnBlur, setValidateOnBlur] = useState(false);
    const [validateOnChange, setValidateOnChange] = useState(false);
    const [isRecaptchaLoading, setIsRecaptchaLoading] = useState(false);
    const [currentExistingUser, setExistingUser] = useStorage(
      storage.LOCAL,
      storageKeys.EXISTING_USER,
      false
    );
    const [, setSessionEmail] = useStorage(
      storage.SESSION,
      storageKeys.EMAIL,
      ''
    );
    const [, setSessionPackageId] = useStorage(
      storage.SESSION,
      storageKeys.PACKAGE_ID,
      desiredPackageId
    );
    const {
      data: emailValidationResponse,
      isLoading: isRequestLoading,
      mutateAsync: handleEmailValidationRequest,
      isSuccess,
    } = useEmailValidationRequest();
    const { data: packageInfoResponse, isLoading: isPackageInfoLoading } = usePackageInfoRequest(
      desiredPackageId as string
    );
    const isLoading = isRequestLoading || isRecaptchaLoading || isSocialLoading;
    const formik: FormikProps<FormValues> = useFormik<FormValues>({
      validateOnBlur,
      validateOnChange,
      initialValues: { email: '' },
      validationSchema: validationSchema(t),
      onSubmit: onSubmit(
        handleEmailValidationRequest,
        setIsRecaptchaLoading,
        setSessionPackageId,
        setSessionEmail,
        desiredPackageId,
        queryParams,
        isLoading
      ),
    });
    const { errors, values, setFieldError } = formik;
    const { getStartedText } = packageInfoResponse?.data || {};

    useEffect(() => {
      if (formik.dirty && !validateOnBlur) setValidateOnBlur(true);
    }, [formik.dirty, validateOnBlur]);

    useEffect(() => {
      if (errors.email && !validateOnChange) {
        setValidateOnChange(true);
      } else if (!errors.email) {
        setValidateOnChange(false);
      }
    }, [validateOnChange, errors]);

    useEffect(() => {
      if (isSuccess) {
        const {status, data} = emailValidationResponse || {};
        if (String(status).startsWith(responseTypes.SUCCESS)) {
          const {url, existingUser} = data || {};
          if (existingUser) {
            setExistingUser(existingUser);
          } else if (currentExistingUser) {
            setExistingUser(false);
          }
          if (url) {
            window.location.href = url;
          } else {
            navigate(routesPaths.VERIFY);
          }
          if ((window as any).dataLayer) {
            (window as any).dataLayer.push({
              event: gtmEvents.EMAIL_SUBMITION,
              email: values.email,
            });
          }
        } else {
          const {message_en} = data || {};
          setFieldError(EMAIL, message_en);
          if (data?.id === ALREADY_REGISTERED_ERROR_ID) {
            openErrorModal({
              url: routesPaths.LOGIN,
              text: t(`errors.${ALREADY_REGISTERED_ERROR_ID}`),
              confirmBtnText: t('common.login'),
              preventActionsOnClose: true,
              withDissmiss: true,
            });
          } else if (data?.statusCode === apiStatuses.BAD_REQUEST) {
            setFieldError(EMAIL, data?.message?.join('\n'));
          }
        }
      }
    }, [emailValidationResponse, isSuccess, navigate, setFieldError]);

    useEffect(() => {
      if (
        packageInfoResponse?.status === apiStatuses.SERVER_ERROR ||
        packageInfoResponse?.status === apiStatuses.NOT_FOUND
      ) {
        openErrorModal({
          url: links.PRICING,
          text: t(`errors.${packageInfoResponse.data?.id}`),
        });
      }
    }, [emailValidationResponse, packageInfoResponse, openErrorModal]);

    const SecondScreen = useMemo(() => {
      const mediaLink = packageInfoResponse?.data?.gettingStartedMediaUrl;
      if (mediaLink) {
        return (
          <StyledVideoContainer>
            { packageInfoResponse?.status === apiStatuses.SUCCESS &&
              <StyledVideoScreen autoPlay loop muted poster={mediaLink}>
                <source src={mediaLink} type="video/mp4" />
              </StyledVideoScreen>
            }
          </StyledVideoContainer>
        );
      } else {
        return <StyledSideImage className="meetings-icon" />;
      }
    }, [packageInfoResponse]);

    useEffect(() => {
      sendFunnelAnalytics({
        type: 'pageLoad',
        page: '',
        event: 'signupCapture',
      }, desiredPackageId);
    }, []);

    const isAvatarProduct = useMemo(() => {
      return avatarStudioPackageIds.includes(desiredPackageId as Packages);
    }, [desiredPackageId]);

    return (
      <>
        <Helmet>
          <title>{t('titles.create-account')}</title>
        </Helmet>
        {isPackageInfoLoading ? (
          <StyledLoadingContainer>
            <StyledLoadingHeader>
              <StyledLogo />
            </StyledLoadingHeader>
            <StyledLoaderContent>
              <StyledLoader size="large" />
            </StyledLoaderContent>
          </StyledLoadingContainer>
        ) : (
          <SplitScreenLayout secondScreen={SecondScreen}>
            <StyledContentContainer centered>
              <FadeInContainer>
                <StyledGetStartedContainer isSecondary={isSecondary} hasSocialLogin={packageInfoResponse?.data?.enableSocialLoginFlow}>
                  <StyledGetStartedTitle as="span" className="main-header">
                    {isAvatarProduct ? t('get-started.avatar-studio-title') : t('get-started.lets-get-started')}
                  </StyledGetStartedTitle>
                  {getStartedText && (
                    <StyledGetStartedText className="getting-started-description">
                      {getStartedText}
                    </StyledGetStartedText>
                  )}
                  <div className="form-container">
                    {
                      packageInfoResponse?.data?.enableSocialLoginFlow && (
                        <SocialRegistration
                          onLoadingChange={setIsSocialLoading}
                          openErrorModal={openErrorModal}
                          setFieldError={setFieldError}
                          loading={isLoading}
                          options={packageInfoResponse.data.packageAdditionalData?.socialRegistrationOptions || [socialType.GOOGLE, socialType.FACEBOOK]}
                        />
                      )
                    }
                    <GetStartedForm formik={formik} isLoading={isLoading} showPrivacyFooter={!isAvatarProduct} />
                  </div>
                </StyledGetStartedContainer>
              </FadeInContainer>
            </StyledContentContainer>
          </SplitScreenLayout>
        )}
      </>
    );
  }
);
